import React, { useEffect, useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import { v4 as uuidv4 } from "uuid";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
  Grid,
  InputAdornment,
  IconButton,
  Alert,
  Avatar,
  Typography,
  Divider,
  Snackbar   // ✅ Added
} from "@mui/material";

import MuiAlert from "@mui/material/Alert"; // ✅ Added

import {
  Visibility,
  VisibilityOff,
  Save,
  Close,
  Person,
} from "@mui/icons-material";

import md5 from "md5";
import { fetchUserDetails } from "features/management/userManagement/userThunks";
import { useDispatch } from "react-redux";
import { getRoleDetails, createUser } from "config/apis";

const AddUserDetailsPopup = ({ open, onClose }) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    orgName: "",
    phoneNumber: "",
    password: "",
    confirmPassword: "",
    role: "",
    orgAddress: "",
    orgEmail: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [role, setRole] = useState([]);
  const dispatch = useDispatch();

  // ✅ Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName) newErrors.firstName = "First Name is required";
    if (!formData.lastName) newErrors.lastName = "Last Name is required";
    if (!formData.orgName) newErrors.orgName = "Organization Name is required";
    if (!formData.phoneNumber) {
      newErrors.phoneNumber = "Phone Number is required";
    } else if (!/^\d{10}$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = "Phone Number must be exactly 10 digits";
    }
    if (!formData.password) newErrors.password = "Password is required";
    if (!formData.confirmPassword)
      newErrors.confirmPassword = "Please confirm your password";
    if (!formData.role) newErrors.role = "Role is required";
    if (!formData.orgAddress)
      newErrors.orgAddress = "Organization Address is required";
    if (!formData.orgEmail)
      newErrors.orgEmail = "Organization Email is required";

    if (
      formData.password &&
      formData.confirmPassword &&
      formData.password !== formData.confirmPassword
    ) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (formData.orgEmail && !/\S+@\S+\.\S+/.test(formData.orgEmail)) {
      newErrors.orgEmail = "Email is invalid";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const fetchRole = async () => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(getRoleDetails, {
        headers: {
          "x-dhundhoo-session": token,
        },
      });
      const data = response.data;
      setRole(data);
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error.response?.data?.message || "Failed to fetch roles.",
        severity: "error",
      });
    }
  };

  useEffect(() => {
    fetchRole();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      try {
        const token = localStorage.getItem("token");

        // const selectedRole = formData.role.toLowerCase();
        // const roleId = role.find(
        //   (val) => val.roleName.toLowerCase() === selectedRole
        // );

        const payload = {
          orgName: formData.orgName,
          address: formData.orgAddress,
          type: "",
          emailId: formData.orgEmail,
          phoneNumber: `91${formData.phoneNumber}`,
          config: {
            push_option: "PUSH_ONLY",
            sms_option: "SMS_NO_LINK",
            vendor_option: "ENABLE_VENDOR_PLUS",
            attendance_option: "DISABLED",
            logs_option: "ENABLED",
          },
          avatar: "",
          apiKey: "",
          orgCode: "",
          vendorHandle: "",
          externalInfo: {},
          speedLimit: 0,
          disableJourneys: "",
          enableJourneyTime: 0,
          timeZone: null,
          firstName: formData.firstName,
          lastName: formData.lastName,
          mobileNumber: formData.phoneNumber,
          email: formData.orgEmail,
          userName: formData.orgEmail,
          userPassword: formData.password,
          roleId: formData.role?.toString() || "",
          groupId: 0,
          orgAddress: formData.orgAddress,
          signUp: {
            firstName: formData.firstName,
            lastName: formData.lastName,
            phoneNumber: `91${formData.phoneNumber}`,
            emailId: formData.orgEmail,
            encodedPassword: md5(formData.password),
            installToken: uuidv4(),
            mode: "EMAIL",
          },
        };

        const response = await axios.post(createUser, payload, {
          headers: {
            "x-dhundhoo-session": token,
            "Content-Type": "application/json",
          },
        });

        // ✅ Show backend response message in Snackbar
        setSnackbar({
          open: true,
          message: response.data || "User Created Successfully",
          severity: "success",
        });

        setSuccess(true);
        dispatch(fetchUserDetails());

        setTimeout(() => {
          setFormData({
            firstName: "",
            lastName: "",
            orgName: "",
            phoneNumber: "",
            password: "",
            confirmPassword: "",
            role: "",
            orgAddress: "",
            orgEmail: "",
          });
          setSuccess(false);
          onClose();
        }, 1500);
      } catch (error) {
        setSnackbar({
          open: true,
          message:
            error.response?.data?.message ||
            error.response?.data ||
            "Failed to save user.",
          severity: "error",
        });
      }
    }
  };

  const handleClose = () => {
    setFormData({
      firstName: "",
      lastName: "",
      orgName: "",
      phoneNumber: "",
      password: "",
      confirmPassword: "",
      role: "",
      orgAddress: "",
      orgEmail: "",
    });
    setErrors({});
    setSuccess(false);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Avatar sx={{ bgcolor: "primary.main", mr: 2 }}>
              <Person />
            </Avatar>
            <Typography variant="h6" component="span" fontWeight="bold">
              New user details
            </Typography>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                User details saved successfully!
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="First Name"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    error={!!errors.firstName}
                    helperText={errors.firstName}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Last Name"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    error={!!errors.lastName}
                    helperText={errors.lastName}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="Organization Name"
                    name="orgName"
                    value={formData.orgName}
                    onChange={handleChange}
                    error={!!errors.orgName}
                    helperText={errors.orgName}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="Phone Number"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "").slice(0, 10);

                      setFormData({
                        ...formData,
                        phoneNumber: value,
                      });

                      if (errors.phoneNumber) {
                        setErrors({
                          ...errors,
                          phoneNumber: "",
                        });
                      }
                    }}
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber}
                    size="small"
                    inputProps={{
                      maxLength: 10,
                      inputMode: "numeric",
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    type={showPassword ? "text" : "password"}
                    label="Password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    error={!!errors.password}
                    helperText={errors.password}
                    size="small"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            size="small"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    type={showConfirmPassword ? "text" : "password"}
                    label="Confirm Password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword}
                    size="small"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                            edge="end"
                            size="small"
                          >
                            {showConfirmPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControl
                    fullWidth
                    required
                    error={!!errors.role}
                    size="small"
                  >
                    <InputLabel>Select Role</InputLabel>
                    <Select
                      name="role"
                      value={formData.role}
                      label="Select Role"
                      onChange={handleChange}
                      sx={{ height: 38 }}
                    >
                      {role.map((r) => (
                        <MenuItem key={r.roleId} value={r.roleId}>
                          {r.roleName}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.role && (
                      <Typography variant="caption" color="error">
                        {errors.role}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="Organization Address"
                    name="orgAddress"
                    value={formData.orgAddress}
                    onChange={handleChange}
                    error={!!errors.orgAddress}
                    helperText={errors.orgAddress}
                    multiline
                    rows={2}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="Organization Email"
                    name="orgEmail"
                    type="email"
                    value={formData.orgEmail}
                    onChange={handleChange}
                    error={!!errors.orgEmail}
                    helperText={errors.orgEmail}
                    size="small"
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Close />}
            onClick={handleClose}
            // color="secondary"
            sx={{ color: "#000" }}
          >
            Close
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<Save />}
            onClick={handleSubmit}
            sx={{ ml: 1, color: "#fff" }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Snackbar Added (UI untouched) */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

AddUserDetailsPopup.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AddUserDetailsPopup;
